import type { RouteObject } from 'react-router-dom'
import {
  type LoaderFunctionArgs,
  Navigate,
  Outlet,
  redirect,
} from 'react-router-dom'
import Debug from 'debug'

import { isAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { ErrorElement } from '@jotta/ui/ErrorElement'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'

import { fetchPhotoByMD5 } from '../api/photosApi'
import { AlbumRoute } from '../components/Album/Album/Album.route'
import { albumInfoLoader } from '../components/Album/AlbumInfo/AlbumInfo.loaders'
import { AlbumList } from '../components/Album/AlbumList/AlbumList'
import { PhotoLayout } from '../components/PhotoLayout/PhotoLayout'
import { SharedPhotosContainer } from '../components/SharedPhotosPage/SharedPhotosContainer'
import { PhotoRouteId } from '../routes/photoRouteIds'

import { PhotoTimelineRoute } from './PhotoTimelineRoute'

const debug = Debug('jotta:photos:route')

const LazyPeopleList = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PeopleList" */ '../components/Album/People/PeopleList'
  ).then(module => ({
    default: module.PeopleList,
  })),
)

const LazyPersonPhotosRoute = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PersonPhotosRoute" */
    '../components/Album/People/PersonPhotos.route'
  ).then(module => ({
    default: module.PersonPhotosRoute,
  })),
)

function PeopleRoute() {
  const { facesV2, isPending: isFeatureConfigPending } = useFeatures()

  if (isFeatureConfigPending) {
    return <LoadingOverlay id="PeopleRoute" position="absolute" open />
  }

  if (!facesV2) {
    return <Navigate to="/photo/album" replace />
  }

  return <LazyPeopleList />
}

function PersonPhotosRoute() {
  const { facesV2, isPending: isFeatureConfigPending } = useFeatures()

  if (isFeatureConfigPending) {
    return <LoadingOverlay id="PersonPhotosRoute" position="absolute" open />
  }

  if (!facesV2) {
    return <Navigate to="/photo/album" replace />
  }

  return <LazyPersonPhotosRoute />
}

export const PrivatePhotoRoute: RouteObject = {
  path: 'photo',
  id: PhotoRouteId.PhotosRoot,
  element: <PhotoLayout />,
  errorElement: (
    <ErrorElement
      captureContext={scope => scope.setTag('section', 'timelineroute')}
    />
  ),
  children: [
    {
      path: 'album',
      id: PhotoRouteId.PhotosAlbum,
      errorElement: (
        <ErrorElement
          captureContext={scope => scope.setTag('section', 'photos_album')}
        />
      ),
      children: [
        {
          index: true,
          id: PhotoRouteId.PhotosAlbumList,
          element: <AlbumList />,
        },
        {
          path: 'all',
          id: PhotoRouteId.PhotosAlbumsNormal,
          element: <AlbumList showOnly="NORMAL_ALBUM" />,
        },
        {
          path: 'shared',
          id: PhotoRouteId.PhotosAlbumsShared,
          element: <AlbumList showOnly="META_SHARED_ALBUM_TYPES" />,
        },
        {
          id: PhotoRouteId.PhotosAlbumsLocation,
          path: 'location',
          element: <AlbumList showOnly="LOCATION_ALBUM" />,
        },
        {
          id: PhotoRouteId.PhotosAlbumsPeople,
          path: 'people',
          element: <PeopleRoute />,
        },
        {
          id: PhotoRouteId.PhotosPerson,
          path: 'people/:personId',
          element: <PersonPhotosRoute />,
          children: [
            {
              path: ':mediaId',
              element: <Outlet />,
            },
          ],
        },
        ...['all', 'shared', 'location'].map(albumType => {
          const routeId = `${PhotoRouteId.PhotosPublicAlbumInfoLoader}_${albumType}`
          return {
            path: `${albumType}/:albumId`,
            id: routeId,
            loader: albumInfoLoader,
            element: <AlbumRoute loaderId={routeId} />,
            children: [
              {
                path: ':mediaId',
                element: <Outlet />,
              },
            ],
          }
        }),
        {
          path: ':albumId',
          id: PhotoRouteId.PhotosAlbumInfoLoader,
          loader: albumInfoLoader,
          element: <AlbumRoute loaderId={PhotoRouteId.PhotosAlbumInfoLoader} />,
          children: [
            {
              path: ':mediaId',
              element: <Outlet />,
            },
          ],
        },
      ],
    },
    {
      path: 'all_shared',
      id: PhotoRouteId.PhotosAllShared,
      errorElement: (
        <ErrorElement
          captureContext={scope => scope.setTag('section', 'photos_all_shared')}
        />
      ),
      element: <SharedPhotosContainer />,
    },
    {
      path: 'all_hidden',
      id: PhotoRouteId.PhotosAllHidden,
      element: <PhotoTimelineRoute hidden />,
      errorElement: (
        <ErrorElement
          captureContext={scope => scope.setTag('section', 'timeline_hidden')}
        />
      ),
      children: [
        {
          path: ':mediaId',
          id: PhotoRouteId.PhotosHiddenMedia,
          element: <Outlet />,
          errorElement: (
            <ErrorElement
              captureContext={scope =>
                scope.setTag('section', 'timeline_hidden_carousel')
              }
            />
          ),
        },
      ],
    },
    {
      element: <PhotoTimelineRoute />,
      children: [
        {
          id: PhotoRouteId.PhotosTimeline,
          element: <Outlet />,
          index: true,
        },
        {
          id: PhotoRouteId.PhotosMedia,
          path: ':mediaId',
          element: <Outlet />,
          // Redirect urls with MD5 photo hash to the actual ID
          loader: async ({ params }: LoaderFunctionArgs) => {
            const { mediaId = '' } = params
            const isMd5 = /^[0-9a-f]{32}$/.test(mediaId)
            const authenticated = await isAuthenticated()
            if (isMd5 && authenticated) {
              try {
                debug(':mediaId looks like MD5, trying to fetch', mediaId)
                const photo = await fetchPhotoByMD5(mediaId)
                return redirect(`/photo/${photo.id}`)
              } catch (error) {
                return null
              }
            }
            return null
          },
          errorElement: (
            <ErrorElement
              captureContext={scope =>
                scope.setTag('section', 'timeline_carousel')
              }
            />
          ),
        },
      ],
    },
  ],
}
